class ESASubscriptionWidget extends HTMLElement {
  constructor() {
    super();

    this.product = JSON.parse(this.dataset.product);
    this.currencySymbol = this.dataset.currencySymbol;
    this.currencyCode = this.dataset.currencyCode;
    this.currencyName = this.dataset.currencyName;
    this.moneyFormat = this.dataset.moneyFormat;
  }

  connectedCallback() {
    this.bindEvents();
    this.updatePrices();
    this.updateSellingPlanState();
    this.loadTranslations();
  }

  bindEvents() {
    this.querySelectorAll('input[name="quantity"]').forEach((input) => {
      input.addEventListener('change', () => {
        this.updatePrices();
        this.updateBoxesProperty();
      });
    });

    this.querySelectorAll('input[name="purchase_type"]').forEach((input) => {
      input.addEventListener('change', () => {
        this.updatePurchaseType();
        this.updateSellingPlanState();
      });
    });

    this.querySelector('[data-selling-plan-select]')?.addEventListener('change', () => {
      this.updatePrices();
    });

    document.querySelector('form[data-product-form] select[name="id"]')?.addEventListener('change', () => {
      this.updatePrices();
    });

    window.addEventListener('popstate', () => {
      this.updatePrices();
    });
  }

  get currentVariant() {
    const urlVariantId = Number(new URLSearchParams(window.location.search).get('variant'));

    if (urlVariantId) {
      const variant = this.product.variants.find((variant) => variant.id === urlVariantId);
      if (variant) return variant;
    }

    const selectedVariantId = Number(document.querySelector('form[data-product-form] select[name="id"]')?.value);

    if (selectedVariantId) {
      const variant = this.product.variants.find((variant) => variant.id === selectedVariantId);

      if (variant) return variant;
    }

    return (this.product.variants.find((variant) => variant.available) || this.product.variants[0]);
  }

  get quantity() {
    return Number(this.querySelector('input[name="quantity"]:checked')?.value || 1);
  }

  get purchaseType() {
    return this.querySelector('input[name="purchase_type"]:checked')?.value;
  }

  get selectedAllocation() {
    const sellingPlanId = Number(this.querySelector('[data-selling-plan-select]')?.value);

    return (this.currentVariant.selling_plan_allocations?.find((allocation) =>  allocation.selling_plan_id === sellingPlanId) || this.currentVariant.selling_plan_allocations?.[0]);
  }

  formatMoney(cents) { 
    const amount = Number(cents) / 100; 
    return this.moneyFormat.replace(/\{\{\s*amount\s*\}\}/g, amount.toFixed(2)).replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(amount)).replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.toFixed(2).replace('.', ',')).replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(amount)).replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, amount.toFixed(2).replace('.', ' ')).replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/g, Math.round(amount));
  }

  updatePrices() {
    const oneTimePrice = this.currentVariant.price * this.quantity;

    const subscriptionPrice = (this.selectedAllocation?.price || this.currentVariant.price) * this.quantity;

    this.querySelector('[data-onetime-price]') &&
      (this.querySelector('[data-onetime-price]').textContent = this.formatMoney(oneTimePrice));

    this.querySelector('[data-subscription-price]') &&
      (this.querySelector('[data-subscription-price]').textContent = this.formatMoney(subscriptionPrice));

    this.querySelector('[data-compare-price]') &&
      (this.querySelector('[data-compare-price]').innerHTML = `<s>${this.formatMoney(oneTimePrice)}</s>`);
  }

  updateSellingPlanState() {
    const sellingPlanSelect = this.querySelector('[data-selling-plan-select]');

    if (!sellingPlanSelect) return;

    if (this.purchaseType === 'onetime') {
      sellingPlanSelect.disabled = true;
    } else {
      sellingPlanSelect.disabled = false;
    }
  }

  updatePurchaseType() {
    this.querySelectorAll('.esa__option').forEach((option) =>
      option.classList.remove('is-selected')
    );

    this.querySelector('input[name="purchase_type"]:checked') ?.closest('.esa__option') ?.classList.add('is-selected');
  }

  updateBoxesProperty() {
    const quantityInput = this.querySelector('input[name="quantity"]:checked');
    const boxesPropertyInput = this.querySelector('[data-boxes-property]');

    if (!quantityInput || !boxesPropertyInput) return;

    boxesPropertyInput.value = quantityInput.dataset.value;
  }

  async _makeApiRequest(url, options = {}) {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      ...options
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }

    return response.json();
  }

  loadTranslations() {
    this._makeApiRequest(`https://apps.easysubscription.io/api/external/multi-language/all/?shop=${Shopify.shop}&section=Product%20Page`)
    .then(response => {
      if (!response?.status || !response?.result?.length) {
        return;
      }

      const selectedLanguage = (Shopify.locale || 'EN').toUpperCase();

      const filteredLanguageData = response.result.filter(
        item => item.language_code === selectedLanguage
      );

      this.multiLanguageData = filteredLanguageData.reduce((acc, item) => {
        acc[item.translated_key] = item.translated_text;
        return acc;
      },{});

      this.applyTranslations();
    })
    .catch(console.error);
  }

  applyTranslations() {
    this.querySelectorAll('.esa__frequency-dropdown option').forEach(option => {
      option.textContent = this.replaceText(option.textContent);
    });
  }

  replaceText(text) {
    if (!text || typeof text !== 'string') return text;

    return text.replace(/\bDelivery every\b/g, this.multiLanguageData.delivery_every || 'Delivery every')
    .replace(/\bdays\b/g, this.multiLanguageData.subscription_widget_des_days_text || 'days')
    .replace(/\bday\b/g, this.multiLanguageData.subscription_widget_des_day_text || 'day')
    .replace(/\bweeks\b/g, this.multiLanguageData.subscription_widget_des_weeks_text || 'weeks')
    .replace(/\bweek\b/g, this.multiLanguageData.subscription_widget_des_week_text || 'week')
    .replace(/\bmonths\b/g, this.multiLanguageData.subscription_widget_des_months_text || 'months')
    .replace(/\bmonth\b/g, this.multiLanguageData.subscription_widget_des_month_text || 'month')
    .replace(/\byears\b/g, this.multiLanguageData.subscription_widget_des_years_text || 'years')
    .replace(/\byear\b/g, this.multiLanguageData.subscription_widget_des_yr_text || 'year');
  }
}

customElements.define('esa-subscription-widget', ESASubscriptionWidget);